import { z } from "zod";
import {
  optionalEnum,
  optionalExternalId,
  optionalUuid,
} from "./_helpers/optional";
import { clampedLimit } from "./common.schema";
import { ProductInsertSchema } from "./product.schema";
import { TerritoryInsertSchema } from "./territory.schema";
import {
  CreateCustomerInputSchema,
  CreateTeamMemberInputSchema,
} from "./contact.schema";
import { CrewInsertSchema } from "./crew.schema";
import { tradePartnerRoles } from "./trade-partner.schema";
import {
  LocationEnrichmentInputSchema,
  LocationInsertSchema,
} from "./location.schema";
import {
  ProposalInsertSchema,
  ProposalItemInsertObjectSchema,
  proposalItemKinds,
} from "./proposal.schema";
import { JobInsertSchema, JobItemInsertSchema } from "./job.schema";
import { InvoiceInsertSchema } from "./invoice.schema";
import {
  WarrantyClaimInsertSchema,
  WarrantyInsertSchema,
  WarrantyTermInsertSchema,
} from "./warranty.schema";
import { TransactionInsertSchema } from "./transaction.schema";
import { ServicePlanInsertSchema } from "./service-plan.schema";
import { ServiceAgreementInsertSchema } from "./service-agreement.schema";
import { OpportunityInsertSchema } from "./opportunity.schema";
import { CommissionPlanInsertSchema } from "./commission-plan.schema";
import { CommissionInsertSchema } from "./commission.schema";
import { BrandInsertSchema } from "./brand.schema";
import { ActivityEventInsertSchema } from "./activity-event.schema";
import { CreateAppointmentSchema } from "./appointment.schema";
import { contactRelationshipTypes } from "./contact.schema";
import { CommunicationImportRowSchema } from "./communication.schema";
import {
  MediaSubmissionInsertSchema,
  MediaItemInsertSchema,
  MediaSubjectInsertSchema,
} from "./media.schema";

// ---------------------------------------------------------------------------
// Entity type enum & tier ordering
// ---------------------------------------------------------------------------

export const ImportEntityType = z.enum([
  "product",
  "territory",
  // ADR-0052 / ADV-1206: a trade partner is a contact playing the
  // `trade_partner` role (generalized from the former `vendor`), with a
  // sub-role set (supplier|manufacturer|distributor|warranty_provider|
  // financing_provider|subcontractor). Imported tier 1 — before products link
  // their manufacturer, crews link their subcontractor owner, and warranty
  // terms link their provider. The handler creates the contact + role +
  // core.trade_partner_attributes + one core.trade_partner_roles per sub-role
  // and registers the contact_id under entity_type `trade_partner`, so FK
  // resolution (manufacturer/distributor/provider/trade_partner external_id)
  // resolves to the contact, then role-resolution converts contact_id →
  // trade-partner contact_role_id (ROLE_FIELD_MAPPINGS).
  "trade_partner",
  "customer",
  "team_member",
  "crew",
  "location",
  "opportunity",
  "proposal",
  "proposal_item",
  "job",
  "job_item",
  "invoice",
  // Warranty 3-layer model (ADR-0047). entity_type `warranty` is the OBLIGATION
  // (matches the canonical entity_type used by activity-events / media_subjects /
  // the /warranties register); `warranty_term` is the template; `warranty_claim`
  // is the remediation-work table (the old `warranty` import target before the
  // ADV-1168 rename).
  "warranty",
  "warranty_term",
  "warranty_claim",
  // Service/maintenance agreements (ADR-0048). `service_plan` is the reusable
  // template (tier 2, like `warranty_term`); `service_agreement` is the
  // per-customer instance (tier 4 — visit `job`s reference it, so it must
  // import before jobs via IMPORT_INTRA_TIER_DEPS). Entity-type strings match
  // the canonical names used by activity-events / media_subjects (ADV-1181).
  "service_plan",
  "service_agreement",
  "transaction",
  "commission_plan",
  "brand",
  "brand_assignment",
  "promotion",
  "proposal_discount",
  "commission",
  "activity_event",
  "appointment",
  "contact_relationship",
  "territory_product",
  // ADR-0052 / ADV-1206: distributor sourcing join. One product, many
  // distributor (trade_partner) sources. References product[1] + distributor
  // trade_partner[1] — tier 2. distributor_external_id resolves to the
  // trade-partner contact, then role-resolution writes
  // trade_partner_contact_role_id.
  "product_distributor_source",
  // ADV-1207 / ADR-0051 §1: warranty term ↔ product attachment join
  // (finance.warranty_term_products). Attaches a manufacturer-scoped term to
  // specific products. References warranty_term[2] + product[1] — tier 3.
  "warranty_term_products",
  "crew_member",
  "crew_leader",
  "communication",
  // ADV-914: media triad. submission < item < subject ordering enforced by
  // IMPORT_TIER_ORDER (tiers 8/9/10). media_subject is polymorphic on
  // (entity_type, entity_external_id) — same pattern as communication /
  // activity_event — and must therefore land after every potential parent
  // entity including activity_event (tier 7).
  "media_submission",
  "media_item",
  "media_subject",
]);
export type ImportEntityType = z.infer<typeof ImportEntityType>;

/** Dependency tiers — lower tiers must be imported first. */
export const IMPORT_TIER_ORDER: Record<ImportEntityType, number> = {
  product: 1,
  territory: 1,
  brand: 1,
  // ADR-0052 / ADV-1206: trade partners are tier-1 standalone contacts (the
  // only FK is an optional territory). They must land before products link a
  // manufacturer, crews link a subcontractor owner, warranty terms link a
  // provider, and the product_distributor_sources join (all of which resolve
  // a trade-partner contact_role).
  trade_partner: 1,
  customer: 2,
  team_member: 2,
  crew: 2,
  commission_plan: 2,
  promotion: 2,
  territory_product: 2,
  // ADR-0047: warranty term template is a tier-2 standalone (only optional FK is
  // product). Obligations (tier 5, entity_type `warranty`) stamp it onto a job.
  warranty_term: 2,
  // ADR-0048: service plan template is a tier-2 standalone (no required FK).
  service_plan: 2,
  // ADR-0052 / ADV-1206: distributor-source join references product[1] +
  // distributor trade_partner[1] — both tier 1 — so it sits in tier 2.
  product_distributor_source: 2,
  location: 3,
  opportunity: 3,
  brand_assignment: 3,
  contact_relationship: 3,
  crew_member: 3,
  crew_leader: 3,
  // ADV-1207 / ADR-0051 §1: warranty_term_products join references
  // warranty_term[2] + product[1] — sits in tier 3 so the term template is in
  // place before the attachment is written.
  warranty_term_products: 3,
  proposal: 4,
  proposal_item: 4,
  job: 4,
  appointment: 4,
  // ADR-0048: a service_agreement references service_plan[2], customer[2],
  // opportunity[3], location[3] — all lower tiers — so it sits in tier 4. Visit
  // jobs carry service_agreement_external_id, so `job` depends on it intra-tier
  // (IMPORT_INTRA_TIER_DEPS) to guarantee agreement-before-job ordering.
  service_agreement: 4,
  job_item: 5,
  invoice: 5,
  // ADR-0047: `warranty` is the OBLIGATION (references warranty_term[2], customer[2],
  // opportunity[3], job[4]); `warranty_claim` is filed against an obligation, so it
  // imports after `warranty` (intra-tier dep below).
  warranty: 5,
  warranty_claim: 5,
  proposal_discount: 5,
  transaction: 6,
  commission: 6,
  // ADV-683: communications can reference any earlier entity via the
  // polymorphic (entity_type, entity_external_id) pair, so they import in a
  // late tier once every potential parent is in place.
  communication: 6,
  // ADV-904: activity_event has the same polymorphic (entity_type,
  // entity_external_id) shape as communication and additionally may reference
  // a communication ("call placed", "email sent" timeline entries). Placing it
  // in the final tier guarantees every potential FK target — including
  // communications — has been imported first. The topo-sort can't infer this
  // from the field name alone (see POLYMORPHIC_ENTITY_FK_TYPES in
  // packages/services/src/core/import-service.ts).
  activity_event: 7,
  // ADV-914: media triad imports last. submission must be in place before
  // item (media_items.submission_id NOT NULL FK), and subject must be in
  // place after both item and every potential polymorphic parent including
  // activity_event (tier 7) and the regular CRM entities (job, opportunity,
  // proposal, warranty).
  media_submission: 8,
  media_item: 9,
  media_subject: 10,
};

/**
 * Intra-tier import dependencies (ADV-895).
 *
 * Each entry maps an entity to other entities in the **same tier** that must
 * be imported first. Cross-tier dependencies belong in {@link IMPORT_TIER_ORDER}.
 *
 * As of 2026-05-12:
 *   - tier 2: `crew` and `customer` reference `team_member` via
 *     `primary_contact_external_id` / `assigned_rep_external_id`
 *   - tier 4: `job` references `proposal` via `proposal_external_id`
 *
 * Without this map, `entityType.localeCompare(...)` puts `crew` before
 * `team_member` and `job` before `proposal`, breaking FK resolution.
 */
export const IMPORT_INTRA_TIER_DEPS: Partial<
  Record<ImportEntityType, ImportEntityType[]>
> = {
  crew: ["team_member"],
  customer: ["team_member"],
  // ADR-0052: product.manufacturer_external_id references a trade_partner
  // (manufacturer sub-role); both are tier 1, so the trade partner must import
  // first within the tier.
  product: ["trade_partner"],
  // ADR-0048: a maintenance visit job carries service_agreement_external_id;
  // both `job` and `service_agreement` are tier 4, so the agreement must
  // import first.
  job: ["proposal", "service_agreement"],
  // ADR-0047: a claim is filed against an obligation (warranty_external_id →
  // warranty_id), both in tier 5 — obligations must import first.
  warranty_claim: ["warranty"],
};

/**
 * ADV-1110 — for each entity type, the OTHER entity types whose `external_id`
 * values feed into its FK fields. Used by the upload-time
 * `STAGED_PARENTS_PENDING` check: an agent uploading entity `E` while any of
 * `IMPORT_PARENT_ENTITY_TYPES[E]` has a job still in `awaiting_review` /
 * `partially_approved` for the same business gets a non-blocking warning,
 * because the child rows would bucket into `unmatched_rows[]` against still-
 * staged parents (the FK resolver only sees `completed` parents).
 *
 * Coverage is enforced at compile time — the `Record<ImportEntityType, …>`
 * type signature means TypeScript refuses to build when a new entity is added
 * to `ImportEntityType` without registering its parent set here. That guard
 * is the only piece of drift the runtime detector can't notice on its own.
 *
 * Cross-tier only. Intra-tier ordering belongs in `IMPORT_INTRA_TIER_DEPS`.
 *
 * Each entry is derived from the matching `<Entity>ImportRowSchema`'s
 * `_external_id` fields below — keep them in sync. For polymorphic FK
 * entities (`communication`, `activity_event`, `media_subject`), the parents
 * listed are the most common targets of `entity_external_id`; rare polymorphic
 * parents won't trigger the warning, but the recovery path via
 * `unmatched.csv` still works.
 */
export const IMPORT_PARENT_ENTITY_TYPES: Record<
  ImportEntityType,
  ImportEntityType[]
> = {
  // Tier 1 — standalone
  // ADR-0052: product.manufacturer_external_id references a trade_partner
  // (manufacturer sub-role). Both are tier 1, so the manufacturer-before-
  // product ordering is an INTRA-tier dep (IMPORT_INTRA_TIER_DEPS), not a
  // cross-tier parent — `product` stays [] here.
  product: [],
  territory: [],
  brand: [],
  // ADR-0052: a trade partner is a standalone contact (no required FK). The
  // optional sub-role set lives in the row payload, not as an FK parent.
  trade_partner: [],

  // Tier 2 — reference tier 1
  customer: ["territory", "team_member"],
  team_member: ["territory"],
  // ADR-0052: crew.trade_partner_external_id names the subcontractor owner.
  crew: ["territory", "team_member", "trade_partner"],
  commission_plan: ["brand"],
  promotion: ["brand"],
  territory_product: ["territory", "product"],
  // ADR-0047 / ADR-0052: term template attaches to a product and is backed by
  // a manufacturer/warranty_provider trade_partner (provider_external_id).
  warranty_term: ["product", "trade_partner"],
  // ADR-0048: service plan template has no required FK (standalone catalog).
  service_plan: [],
  // ADR-0052 / ADV-1206: distributor-source join references a product and the
  // distributor trade_partner.
  product_distributor_source: ["product", "trade_partner"],

  // Tier 3
  location: ["customer"],
  opportunity: ["customer", "team_member", "brand"],
  brand_assignment: ["team_member", "brand"],
  contact_relationship: ["customer", "team_member"],
  crew_member: ["crew", "team_member"],
  crew_leader: ["crew", "team_member"],
  // ADV-1207 / ADR-0051 §1: warranty_term_products attaches a term to a
  // product — references warranty_term[2] + product[1].
  warranty_term_products: ["warranty_term", "product"],

  // Tier 4
  proposal: ["customer", "opportunity"],
  // ADR-0048: a recurring proposal line references a service_plan
  // (service_plan_external_id → service_plan_id).
  proposal_item: ["proposal", "product", "service_plan"],
  // Per JobImportRowSchema: customer, opportunity, proposal, assigned_crew
  // (crew), lead_tech (team_member), brand. PR #832 review (Codex) flagged
  // assigned_crew / lead_tech / brand as missing in the original.
  // ADR-0048: maintenance visit jobs reference service_agreement.
  job: [
    "customer",
    "opportunity",
    "proposal",
    "crew",
    "team_member",
    "brand",
    "service_agreement",
  ],
  appointment: ["customer", "team_member", "opportunity", "brand"],
  // ADR-0048: a customer agreement instance references its plan, customer,
  // the originating opportunity, and an optional location.
  service_agreement: ["service_plan", "customer", "opportunity", "location"],

  // Tier 5
  job_item: ["job", "product"],
  invoice: ["customer", "opportunity", "job"],
  // ADR-0047: `warranty` is the OBLIGATION — stamps a term onto a customer/job.
  // Per WarrantyImportRowSchema: warranty_term, customer, opportunity, job, location.
  warranty: ["warranty_term", "customer", "opportunity", "job", "location"],
  // ADR-0047: a CLAIM (remediation work) references its obligation plus the same
  // CRM parents the old `warranty` claim entity did.
  warranty_claim: [
    "warranty",
    "customer",
    "opportunity",
    "proposal",
    "job",
    "crew",
    "team_member",
    "territory",
  ],
  proposal_discount: ["proposal", "proposal_item", "promotion"],

  // Tier 6
  // TransactionImportRowSchema: opportunity, job, employee (team_member),
  // crew, warranty, invoice, territory. `vendor_external_id` resolves to a
  // contact_role that isn't a first-class importable entity, so it's
  // intentionally not listed.
  transaction: [
    "opportunity",
    "job",
    "team_member",
    "crew",
    "warranty",
    "invoice",
    "territory",
    // ADR-0048: recurring-billing transactions reference a service_agreement.
    "service_agreement",
  ],
  commission: ["opportunity", "team_member"],
  // Polymorphic via entity_external_id — the listed parents cover the
  // common direct + polymorphic targets. Rare polymorphic parents won't
  // trigger the warning but still bucket into unmatched_rows[] on miss.
  communication: ["customer", "team_member", "opportunity", "brand"],

  // Tier 7
  activity_event: ["customer", "team_member", "communication"],

  // Tier 8/9/10 — media triad
  // Per MediaSubmissionImportRowSchema: location, submitted_by (team_member).
  // PR #832 review (Codex) flagged the original [customer, job, team_member]
  // map as wrong — the schema has no customer/job FK fields.
  media_submission: ["location", "team_member"],
  media_item: ["media_submission"],
  // entity_external_id is polymorphic; common direct + polymorphic parents.
  media_subject: ["media_submission", "media_item", "customer", "job"],
};

/**
 * Topologically sort entity types so:
 *   1. lower-tier entities come first (from {@link IMPORT_TIER_ORDER})
 *   2. within a tier, anything listed in `deps` comes after its dependencies
 *   3. ties broken alphabetically for deterministic output
 *
 * Exposed for unit testing; production callers should use
 * {@link sortImportEntitiesByDependency} which passes the real
 * {@link IMPORT_INTRA_TIER_DEPS}.
 *
 * @throws if `deps` contains a cycle within any single tier — the message
 *   names the involved entities so contributors can find the bad edge.
 */
export function topologicalSortWithTiers(
  entities: readonly ImportEntityType[],
  deps: Partial<Record<ImportEntityType, ImportEntityType[]>>,
): ImportEntityType[] {
  // Group by tier first — cross-tier ordering is fully owned by IMPORT_TIER_ORDER.
  const byTier = new Map<number, ImportEntityType[]>();
  for (const entity of entities) {
    const tier = IMPORT_TIER_ORDER[entity];
    const bucket = byTier.get(tier) ?? [];
    bucket.push(entity);
    byTier.set(tier, bucket);
  }

  const out: ImportEntityType[] = [];
  const sortedTiers = [...byTier.entries()].sort(([a], [b]) => a - b);
  for (const [tier, tierEntities] of sortedTiers) {
    // Alphabetize first so the tiebreak among deps-free entries is deterministic.
    const alpha = [...tierEntities].sort((a, b) => a.localeCompare(b));

    // Kahn's algorithm. Only consider edges where BOTH endpoints are present
    // in this tier's working set — deps pointing to entities not in `entities`
    // are no-ops (they live in a lower tier and are already processed).
    const present = new Set(alpha);
    const incoming = new Map<ImportEntityType, number>();
    const outgoing = new Map<ImportEntityType, ImportEntityType[]>();
    for (const e of alpha) {
      incoming.set(e, 0);
      outgoing.set(e, []);
    }
    for (const e of alpha) {
      for (const dep of deps[e] ?? []) {
        if (!present.has(dep)) continue;
        const depEdges = outgoing.get(dep) ?? [];
        depEdges.push(e);
        outgoing.set(dep, depEdges);
        incoming.set(e, (incoming.get(e) ?? 0) + 1);
      }
    }

    const ready = alpha.filter((e) => (incoming.get(e) ?? 0) === 0);
    let next: ImportEntityType | undefined;
    while ((next = ready.shift()) !== undefined) {
      out.push(next);
      for (const child of outgoing.get(next) ?? []) {
        const remaining = (incoming.get(child) ?? 0) - 1;
        incoming.set(child, remaining);
        if (remaining === 0) {
          // Insert preserving alphabetical order among ready entries
          const idx = ready.findIndex((e) => e.localeCompare(child) > 0);
          if (idx === -1) ready.push(child);
          else ready.splice(idx, 0, child);
        }
      }
    }

    const leftover = alpha.filter((e) => (incoming.get(e) ?? 0) > 0);
    if (leftover.length > 0) {
      throw new Error(
        `IMPORT_INTRA_TIER_DEPS contains a cycle within tier ${tier}: ${leftover.join(", ")}`,
      );
    }
  }
  return out;
}

/**
 * Sort entity types in dependency-safe order. Lower tiers first, then
 * intra-tier dependencies from {@link IMPORT_INTRA_TIER_DEPS}, then alpha.
 *
 * The input array is never mutated.
 */
export function sortImportEntitiesByDependency(
  entities: readonly ImportEntityType[],
): ImportEntityType[] {
  return topologicalSortWithTiers(entities, IMPORT_INTRA_TIER_DEPS);
}

// ---------------------------------------------------------------------------
// external_id — required idempotency key (ADV-1068)
// ---------------------------------------------------------------------------

/**
 * ADV-1068: `external_id` is the idempotency key for every imported row —
 * deduplication, linked-record resolution, and auditability all depend on it
 * being an authentic reference to a real source record. A missing or blank
 * value previously fell through `.optional()` and the row was silently created
 * as a brand-new entity, which is how an LLM that fabricated (or omitted)
 * `external_id` values corrupted customer data without any error. It is now
 * required and non-blank on every import-row schema, and the import pipeline
 * rejects the whole job before any write if any row violates this.
 *
 * `.trim()` runs before `.min(1)` so a whitespace-only value is also rejected
 * (it is just as blank as `""`) and stray surrounding whitespace never ends up
 * baked into the stored idempotency key.
 */
const RequiredExternalIdSchema = z
  .string()
  .trim()
  .min(1, "external_id is required and must not be blank");

// ADV-1398 / ADR-0059 amendment: an entity may carry several
// (source, external_id) aliases (e.g. the same record synced into multiple
// Airtable bases under different rec_ids). This optional column registers extra
// aliases on the row's resolved entity, all pointing at the same entity_id.
//
// Format: a comma-separated list of aliases. Each alias is either
//   "source:external_id"  — explicit source (e.g. "airtable:rec09l8XEKtAW3Hmf")
//   "external_id"          — bare token; defaults to the import job's source
// The string is parsed in the import pipeline (parseAdditionalExternalIds);
// it is split on the FIRST ":" so external_ids containing ":" stay intact.
const AdditionalExternalIdsSchema = z.string().trim().optional();

// ---------------------------------------------------------------------------
// Tier 1 — Standalone entities
// ---------------------------------------------------------------------------

// ADR-0052: products carry an optional manufacturer trade-partner contact role.
// The raw manufacturer_contact_role_id is omitted from imports (role-resolved,
// not supplied as a UUID); manufacturer_external_id resolves to the trade
// partner's contact, then role-resolution (ROLE_FIELD_MAPPINGS.product) writes
// manufacturer_contact_role_id.
export const ProductImportRowSchema = ProductInsertSchema.omit({
  manufacturer_contact_role_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  manufacturer_external_id: optionalExternalId().describe(
    "Manufacturer External ID",
  ),
});
export type ProductImportRow = z.infer<typeof ProductImportRowSchema>;

export const TerritoryImportRowSchema = TerritoryInsertSchema.extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
});
export type TerritoryImportRow = z.infer<typeof TerritoryImportRowSchema>;

// ---------------------------------------------------------------------------
// Trade partner (ADR-0052 / ADV-1206). A contact that plays the `trade_partner`
// role, with a sub-role set and partner-specific attributes. The import handler
// creates: the contact, a `trade_partner` contact_role, a
// core.trade_partner_attributes row, and one core.trade_partner_roles row per
// `partner_roles` entry. The contact_id is registered under entity_type
// `trade_partner`, so downstream FK references (manufacturer/distributor/
// provider/trade_partner external_id) resolve to the contact, then
// role-resolution converts contact_id → trade-partner contact_role_id.
// ---------------------------------------------------------------------------

export const TradePartnerImportRowSchema = z.object({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  // Contact identity (a trade partner is most often a company, so company_name
  // is the typical anchor, but person fields are accepted for partner people).
  first_name: z.string().nullable().optional().describe("First Name"),
  last_name: z.string().nullable().optional().describe("Last Name"),
  email: z.string().nullable().optional().describe("Email"),
  phone: z.string().nullable().optional().describe("Phone"),
  company_name: z.string().nullable().optional().describe("Company Name"),
  // The sub-role set: which kinds of trade partner this is. One
  // core.trade_partner_roles row is written per entry (deduped). The DB CHECK
  // constraint enforces the same six values.
  partner_roles: z
    .array(z.enum(tradePartnerRoles))
    .optional()
    .describe("Partner Roles"),
  // core.trade_partner_attributes fields.
  partner_number: z.string().nullable().optional().describe("Partner Number"),
  payment_terms: z.string().nullable().optional().describe("Payment Terms"),
  default_lead_time_days: z
    .number()
    .int()
    .nullable()
    .optional()
    .describe("Default Lead Time Days"),
  website: z.string().nullable().optional().describe("Website"),
  status: z.string().nullable().optional().describe("Status"),
  notes: z.string().nullable().optional().describe("Notes"),
  tags: z.array(z.string()).optional().describe("Tags"),
  custom_fields: z.record(z.unknown()).optional().describe("Custom Fields"),
});
export type TradePartnerImportRow = z.infer<typeof TradePartnerImportRowSchema>;

// ---------------------------------------------------------------------------
// Tier 2 — Entities with Tier 1 references
// ---------------------------------------------------------------------------

export const CustomerImportRowSchema = CreateCustomerInputSchema.omit({
  territory_id: true,
  assigned_rep_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  territory_external_id: optionalExternalId().describe("Territory External ID"),
  assigned_rep_external_id: optionalExternalId().describe(
    "Assigned Rep External ID",
  ),
  // ADV-240: cross-business contact attachment. When set, the import pipeline
  // resolves this external_id via an ACCOUNT-SCOPED external_ids lookup and
  // attaches a customer role to the existing contact rather than running the
  // business-scoped find-or-create path. Enables one contact to hold customer
  // roles in multiple businesses under the same account.
  account_contact_external_id: optionalExternalId().describe(
    "Account Contact External ID",
  ),
});
export type CustomerImportRow = z.infer<typeof CustomerImportRowSchema>;

export const TeamMemberImportRowSchema = CreateTeamMemberInputSchema.omit({
  primary_territory_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  primary_territory_external_id: optionalExternalId().describe(
    "Primary Territory External ID",
  ),
  // ADV-240: cross-business contact attachment (see CustomerImportRowSchema).
  account_contact_external_id: optionalExternalId().describe(
    "Account Contact External ID",
  ),
});
export type TeamMemberImportRow = z.infer<typeof TeamMemberImportRowSchema>;

// ADR-0052: a crew may be owned by a subcontractor trade partner (null =
// internal employee crew). trade_partner_contact_role_id is omitted from
// imports (role-resolved); trade_partner_external_id resolves to the trade
// partner's contact, then role-resolution (ROLE_FIELD_MAPPINGS.crew) writes
// trade_partner_contact_role_id.
export const CrewImportRowSchema = CrewInsertSchema.omit({
  territory_id: true,
  primary_contact_id: true,
  trade_partner_contact_role_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  territory_external_id: optionalExternalId().describe("Territory External ID"),
  primary_contact_external_id: optionalExternalId().describe(
    "Primary Contact External ID",
  ),
  trade_partner_external_id: optionalExternalId().describe(
    "Trade Partner External ID",
  ),
});
export type CrewImportRow = z.infer<typeof CrewImportRowSchema>;

// ---------------------------------------------------------------------------
// Tier 3 — Location
// ---------------------------------------------------------------------------

export const LocationImportRowSchema = LocationInsertSchema.extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  customer_external_id: optionalExternalId().describe("Customer External ID"),
  // ADV-889 / ADR-0058: location enrichment satellite fields. Accepted at
  // the import boundary, then split off the location row by the bespoke
  // handler in `import-entity-handlers.ts` and upserted into
  // `core.location_enrichments`. Emitter-only fields the importer does NOT
  // own (`is_primary`, `territory_external_id`) are intentionally absent —
  // those flow through other entity homes (entity_locations, customer_attributes).
  ...LocationEnrichmentInputSchema.shape,
})
  // ADR-0058 / Codex P2 on PR #963: keep unknown keys so generator-emitted
  // raw_enrichment fields (e.g. pool_business_name, pool_primary_category)
  // survive safeParse and reach `splitLocationEnrichment`, which routes them
  // into `location_enrichments.raw_enrichment jsonb`. Without passthrough,
  // Zod strips them before the handler runs.
  .passthrough();
export type LocationImportRow = z.infer<typeof LocationImportRowSchema>;

// ---------------------------------------------------------------------------
// Tier 3 — Opportunity (aggregate root linking customer → proposals/jobs/invoices)
// ---------------------------------------------------------------------------

export const OpportunityImportRowSchema = OpportunityInsertSchema.omit({
  customer_role_id: true,
  assigned_rep_id: true,
  brand_id: true,
  property_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  customer_external_id: optionalExternalId().describe("Customer External ID"),
  assigned_rep_external_id: optionalExternalId().describe(
    "Assigned Rep External ID",
  ),
  brand_external_id: optionalExternalId().describe("Brand External ID"),
});
export type OpportunityImportRow = z.infer<typeof OpportunityImportRowSchema>;

// ---------------------------------------------------------------------------
// Tier 4 — Proposals and Jobs (with nested children)
// ---------------------------------------------------------------------------

/** Proposal item fields shared by nested and flat paths. */
// ADV-1185: source from the plain ZodObject (`ProposalItemInsertObjectSchema`)
// rather than the superRefine'd `ProposalItemInsertSchema`, since `.omit()` /
// `.extend()` are only available on ZodObject.
export const ProposalItemImportRowSchema = ProposalItemInsertObjectSchema.omit({
  proposal_id: true,
  product_id: true,
  // ADR-0048: recurring lines reference a plan by external_id, resolved at
  // import (the live UUID `service_plan_id` is set on the resolved row).
  service_plan_id: true,
  // ADV-1387 (Codex P1): the base insert object DEFAULTS `kind` to "one_time".
  // On the import path that default would make a legitimately-OMITTED kind on a
  // service-plan line indistinguishable from an EXPLICIT one_time at the handler
  // — collapsing the biconditional's two cases. We re-declare `kind` below as
  // optional WITHOUT a default so the source signal survives to the handler:
  //   - omitted + service_plan resolved → coerce kind='recurring' (the only
  //     value the kind<->service_plan_id biconditional permits with a plan);
  //   - EXPLICIT one_time + service_plan → genuine contradiction → surfaced
  //     (thrown) BEFORE the parent commits, never orphaning it.
  // A one_time line that omits both kind and service_plan still resolves to the
  // DB column default ('one_time').
  kind: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  product_external_id: optionalExternalId().describe("Product External ID"),
  service_plan_external_id: optionalExternalId().describe(
    "Service Plan External ID",
  ),
  // ADV-1387: optional, NO default — see the `kind` omit comment above.
  // optionalEnum (not bare .optional()) so "" coerces to undefined and the
  // ADR-0044 empty-string rejection cliff is avoided; omitted/""/null all read
  // as "omitted" for the recurring-coercion in coerceProposalItemKind.
  kind: optionalEnum(proposalItemKinds).describe("Kind"),
});
export type ProposalItemImportRow = z.infer<typeof ProposalItemImportRowSchema>;

/** Flat path — standalone proposal items referencing parent by external ID. */
export const ProposalItemFlatImportRowSchema =
  ProposalItemImportRowSchema.extend({
    proposal_external_id: z.string().describe("Proposal External ID"),
  });
export type ProposalItemFlatImportRow = z.infer<
  typeof ProposalItemFlatImportRowSchema
>;

export const ProposalImportRowSchema = ProposalInsertSchema.omit({
  customer_role_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  customer_external_id: z.string().describe("Customer External ID"),
  opportunity_external_id: optionalExternalId().describe(
    "Opportunity External ID",
  ),
  line_items: z
    .array(ProposalItemImportRowSchema)
    .optional()
    .describe("Line Items"),
});
export type ProposalImportRow = z.infer<typeof ProposalImportRowSchema>;

/** Job item fields shared by nested and flat paths. */
export const JobItemImportRowSchema = JobItemInsertSchema.omit({
  product_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  product_external_id: optionalExternalId().describe("Product External ID"),
});
export type JobItemImportRow = z.infer<typeof JobItemImportRowSchema>;

/** Flat path — standalone job items referencing parent by external ID. */
export const JobItemFlatImportRowSchema = JobItemImportRowSchema.extend({
  job_external_id: z.string().describe("Job External ID"),
});
export type JobItemFlatImportRow = z.infer<typeof JobItemFlatImportRowSchema>;

export const JobImportRowSchema = JobInsertSchema.omit({
  contact_role_id: true,
  proposal_id: true,
  assigned_crew_id: true,
  lead_tech_role_id: true,
  brand_id: true,
  // ADR-0048: visit jobs reference the agreement by external_id.
  service_agreement_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  customer_external_id: z.string().describe("Customer External ID"),
  opportunity_external_id: optionalExternalId().describe(
    "Opportunity External ID",
  ),
  proposal_external_id: optionalExternalId().describe("Proposal External ID"),
  assigned_crew_external_id: optionalExternalId().describe(
    "Assigned Crew External ID",
  ),
  lead_tech_external_id: optionalExternalId().describe("Lead Tech External ID"),
  brand_external_id: optionalExternalId().describe("Brand External ID"),
  // ADR-0048: maintenance visit jobs link back to their service_agreement.
  service_agreement_external_id: optionalExternalId().describe(
    "Service Agreement External ID",
  ),
  job_items: z.array(JobItemImportRowSchema).optional().describe("Job Items"),
  // ADV-683: completion_notes lives on JobSchema/JobUpdateSchema but not on
  // JobInsertSchema, so it must be added explicitly to the import row schema.
  completion_notes: z.string().optional().describe("Completion Notes"),
});
export type JobImportRow = z.infer<typeof JobImportRowSchema>;

// ---------------------------------------------------------------------------
// Tier 5 — Invoice, Warranty
// ---------------------------------------------------------------------------

// ADV-1378: InvoiceInsertSchema OMITS the raw payment-ledger columns
// (amount_paid / paid_at / sent_at / payment_method / check_number) — they
// live only on InvoiceUpdateSchema. Without re-adding them here, Zod silently
// STRIPS amount_paid from imported rows, so every invoice imported with
// status='paid'/'partial' lands with balance_due = full total (because
// finance.invoices.balance_due is GENERATED ALWAYS AS subtotal - discount +
// tax_amount - COALESCE(amount_paid,0)) → AR aging shows phantom receivables.
// These are RAW ledger inputs (same exemption rationale as the subtotal /
// tax_amount carve-out in InvoiceInsertSchema, ADV-1257) and are allowed.
// balance_due / total_amount are GENERATED outputs and are NEVER added here.
//
// This stays a plain ZodObject (no superRefine wrapper) so derived schemas and
// `.shape` introspection keep working. Hardening that REJECTS a 'paid'/'partial'
// invoice lacking amount_paid is deferred to its own ticket — it must land
// alongside a regeneration of the committed demo fixtures (the tier-5
// invoice.json + restore CSV currently hold paid invoices without amount_paid,
// which a reject-rule would make the restore scripts fail on).
export const InvoiceImportRowSchema = InvoiceInsertSchema.omit({
  opportunity_id: true,
  customer_role_id: true,
  job_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  customer_external_id: z.string().describe("Customer External ID"),
  opportunity_external_id: optionalExternalId().describe(
    "Opportunity External ID",
  ),
  job_external_id: optionalExternalId().describe("Job External ID"),
  amount_paid: z.number().optional().describe("Amount Paid"),
  sent_at: z.string().optional().describe("Sent At"),
  paid_at: z.string().optional().describe("Paid At"),
  payment_method: z.string().optional().describe("Payment Method"),
  check_number: z.string().optional().describe("Check Number"),
});
export type InvoiceImportRow = z.infer<typeof InvoiceImportRowSchema>;

// ---------------------------------------------------------------------------
// Warranty 3-layer model (ADR-0047 / ADV-1168). The import entity names mirror
// the canonical entity_type names used by activity-events / media_subjects /
// the /warranties register:
//   warranty_term  → finance.warranty_terms  (template, tier 2)
//   warranty       → finance.warranties      (OBLIGATION, tier 5)
//   warranty_claim → finance.warranty_claims (remediation work, tier 5)
// The ADV-1168 rename moved the old `warranty` import target from the claim
// table to the obligation table; `warranty` now imports obligations and the
// renamed claim table is imported via the new `warranty_claim` entity.
// ---------------------------------------------------------------------------

// Tier 2 — Warranty TERM (template). ADV-1207 / ADR-0051 §1 + ADR-0052: the
// per-term product_id + provider_account_id columns were dropped.
// provider_contact_role_id is omitted from imports (role-resolved, not a raw
// UUID); provider_external_id resolves to the manufacturer/warranty_provider
// trade partner's contact, then role-resolution (ROLE_FIELD_MAPPINGS.warranty_term)
// writes provider_contact_role_id. Term-to-product attachment is a separate
// importable entity (warranty_term_products) since a term can cover many
// products; a term with no attachment is manufacturer-scoped.
export const WarrantyTermImportRowSchema = WarrantyTermInsertSchema.omit({
  provider_contact_role_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  provider_external_id: optionalExternalId().describe("Provider External ID"),
});
export type WarrantyTermImportRow = z.infer<typeof WarrantyTermImportRowSchema>;

// Tier 3 — Warranty TERM ↔ PRODUCT attachment join (ADV-1207 / ADR-0051 §1).
// finance.warranty_term_products(warranty_term_id, product_id). Both FKs
// resolve from external_id; account_id/business_id are stamped from tenant
// context. The DB BEFORE-trigger validates the product's manufacturer matches
// the term's provider when both are set.
export const WarrantyTermProductImportRowSchema = z.object({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  warranty_term_external_id: z.string().describe("Warranty Term External ID"),
  product_external_id: z.string().describe("Product External ID"),
});
export type WarrantyTermProductImportRow = z.infer<
  typeof WarrantyTermProductImportRowSchema
>;

// Tier 5 — Warranty OBLIGATION (the `warranty` entity). expiration_date is
// DB-computed (finance.compute_warranty_expiration); never imported.
// customer_external_id resolves to a contact, then role-resolution
// (ROLE_FIELD_MAPPINGS.warranty) writes customer_contact_role_id (NOT NULL).
export const WarrantyImportRowSchema = WarrantyInsertSchema.omit({
  warranty_term_id: true,
  customer_contact_role_id: true,
  property_id: true,
  location_id: true,
  opportunity_id: true,
  job_id: true,
  // ADV-1207 / ADR-0051 §1 + ADR-0052: v1 provider_account_id is gone; the
  // provider is the trade-partner contact role, resolved via role-resolution
  // rather than imported as a raw UUID.
  provider_contact_role_id: true,
  previous_holder_contact_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  warranty_term_external_id: z.string().describe("Warranty Term External ID"),
  customer_external_id: z.string().describe("Customer External ID"),
  opportunity_external_id: optionalExternalId().describe(
    "Opportunity External ID",
  ),
  job_external_id: optionalExternalId().describe("Job External ID"),
  location_external_id: optionalExternalId().describe("Location External ID"),
});
export type WarrantyImportRow = z.infer<typeof WarrantyImportRowSchema>;

// Tier 5 — Warranty CLAIM (remediation work). warranty_external_id links the
// claim to its obligation (resolves to entity_type `warranty` → warranty_id).
export const WarrantyClaimImportRowSchema = WarrantyClaimInsertSchema.omit({
  warranty_id: true,
  job_id: true,
  crew_id: true,
  contact_role_id: true,
  tech_role_id: true,
  proposal_id: true,
  territory_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  warranty_external_id: optionalExternalId().describe("Warranty External ID"),
  opportunity_external_id: optionalExternalId().describe(
    "Opportunity External ID",
  ),
  job_external_id: optionalExternalId().describe("Job External ID"),
  crew_external_id: optionalExternalId().describe("Crew External ID"),
  customer_external_id: optionalExternalId().describe("Customer External ID"),
  tech_external_id: optionalExternalId().describe("Tech External ID"),
  proposal_external_id: optionalExternalId().describe("Proposal External ID"),
  territory_external_id: optionalExternalId().describe("Territory External ID"),
});
export type WarrantyClaimImportRow = z.infer<
  typeof WarrantyClaimImportRowSchema
>;

// ---------------------------------------------------------------------------
// Service/maintenance agreements (ADR-0048 / ADV-1178). Two layers:
//   service_plan       → finance.service_plans       (template, tier 2)
//   service_agreement  → finance.service_agreements  (customer instance, tier 4)
// end_date / renewal_date / next_visit_date / next_bill_date are DB-derived
// (finance.compute_service_agreement_dates) and already omitted from
// ServiceAgreementInsertSchema. customer_external_id resolves to a contact,
// then role-resolution (ROLE_FIELD_MAPPINGS.service_agreement) writes
// customer_contact_role_id (NOT NULL).
// ---------------------------------------------------------------------------

// Tier 2 — service PLAN template. No required FK (standalone catalog).
export const ServicePlanImportRowSchema = ServicePlanInsertSchema.extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
});
export type ServicePlanImportRow = z.infer<typeof ServicePlanImportRowSchema>;

// Tier 4 — service AGREEMENT instance.
export const ServiceAgreementImportRowSchema =
  ServiceAgreementInsertSchema.omit({
    service_plan_id: true,
    customer_contact_role_id: true,
    property_id: true,
    location_id: true,
    opportunity_id: true,
  }).extend({
    external_id: RequiredExternalIdSchema.describe("External ID"),
    additional_external_ids: AdditionalExternalIdsSchema.describe(
      "Additional External IDs",
    ),
    service_plan_external_id: z.string().describe("Service Plan External ID"),
    customer_external_id: z.string().describe("Customer External ID"),
    opportunity_external_id: optionalExternalId().describe(
      "Opportunity External ID",
    ),
    location_external_id: optionalExternalId().describe("Location External ID"),
  });
export type ServiceAgreementImportRow = z.infer<
  typeof ServiceAgreementImportRowSchema
>;

// ---------------------------------------------------------------------------
// Tier 6 — Transaction
// ---------------------------------------------------------------------------

export const TransactionImportRowSchema = TransactionInsertSchema.omit({
  job_id: true,
  employee_role_id: true,
  crew_id: true,
  warranty_id: true,
  invoice_id: true,
  pay_period_id: true,
  territory_id: true,
  vendor_contact_role_id: true,
  // ADR-0048: recurring-billing rows reference the agreement by external_id.
  service_agreement_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  opportunity_external_id: optionalExternalId().describe(
    "Opportunity External ID",
  ),
  job_external_id: optionalExternalId().describe("Job External ID"),
  employee_external_id: optionalExternalId().describe("Employee External ID"),
  crew_external_id: optionalExternalId().describe("Crew External ID"),
  warranty_external_id: optionalExternalId().describe("Warranty External ID"),
  invoice_external_id: optionalExternalId().describe("Invoice External ID"),
  territory_external_id: optionalExternalId().describe("Territory External ID"),
  vendor_external_id: optionalExternalId().describe("Vendor External ID"),
  service_agreement_external_id: optionalExternalId().describe(
    "Service Agreement External ID",
  ),
});
export type TransactionImportRow = z.infer<typeof TransactionImportRowSchema>;

// ---------------------------------------------------------------------------
// Commission Plan (Tier 2)
// ---------------------------------------------------------------------------

export const CommissionPlanImportRowSchema = CommissionPlanInsertSchema.omit({
  brand_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  brand_external_id: optionalExternalId().describe("Brand External ID"),
});
export type CommissionPlanImportRow = z.infer<
  typeof CommissionPlanImportRowSchema
>;

// ---------------------------------------------------------------------------
// Brand (Tier 1 — standalone)
// ---------------------------------------------------------------------------

export const BrandImportRowSchema = BrandInsertSchema.omit({
  business_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
});
export type BrandImportRow = z.infer<typeof BrandImportRowSchema>;

// ---------------------------------------------------------------------------
// Brand Assignment (Tier 3 — depends on team_member + brand)
// ---------------------------------------------------------------------------

export const BrandAssignmentImportRowSchema = z.object({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  team_member_external_id: z.string().describe("Team Member External ID"),
  brand_external_id: z.string().describe("Brand External ID"),
  is_active: z.boolean().default(true).describe("Active"),
  can_create_proposals: z
    .boolean()
    .default(true)
    .describe("Can Create Proposals"),
  can_view_products: z.boolean().default(true).describe("Can View Products"),
});
export type BrandAssignmentImportRow = z.infer<
  typeof BrandAssignmentImportRowSchema
>;

// ---------------------------------------------------------------------------
// Promotion (Tier 2 — depends on brand if brand_external_id provided)
// ---------------------------------------------------------------------------

export const PromotionImportRowSchema = z
  .object({
    external_id: RequiredExternalIdSchema.describe("External ID"),
    additional_external_ids: AdditionalExternalIdsSchema.describe(
      "Additional External IDs",
    ),
    name: z.string().describe("Name"),
    code: z.string().nullable().optional().describe("Code"),
    description: z.string().nullable().optional().describe("Description"),
    discount_type: z.enum(["percentage", "fixed"]).describe("Discount Type"),
    discount_value: z.number().positive().describe("Discount Value"),
    scope: z.enum(["proposal", "line_item"]).describe("Scope"),
    brand_external_id: optionalExternalId().describe("Brand External ID"),
    target_rule: z
      .record(z.unknown())
      .nullable()
      .optional()
      .describe("Target Rule"),
    valid_from: z.string().nullable().optional().describe("Valid From"),
    valid_to: z.string().nullable().optional().describe("Valid To"),
    usage_limit: z.number().nullable().optional().describe("Usage Limit"),
    stackable: z.boolean().optional().describe("Stackable"),
    priority: z.number().optional().describe("Priority"),
    is_active: z.boolean().optional().describe("Active"),
  })
  .refine((d) => d.discount_type !== "percentage" || d.discount_value <= 100, {
    message: "Percentage discount cannot exceed 100",
    path: ["discount_value"],
  });
export type PromotionImportRow = z.infer<typeof PromotionImportRowSchema>;

// ---------------------------------------------------------------------------
// Proposal Discount (Tier 5 — depends on proposal + proposal_item + promotion)
// ---------------------------------------------------------------------------

export const ProposalDiscountImportRowSchema = z.object({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  proposal_external_id: z.string().describe("Proposal External ID"),
  proposal_item_external_id: optionalExternalId().describe(
    "Proposal Item External ID",
  ),
  promotion_external_id: optionalExternalId().describe("Promotion External ID"),
  discount_type: z.enum(["percentage", "fixed"]).describe("Discount Type"),
  discount_value: z.number().positive().describe("Discount Value"),
  // IMPORT SCHEMA GUARD (ADV-1257): discount_amount is DB-computed by the
  // sales.set_proposal_discount_amount() BEFORE-INSERT trigger and is never
  // accepted as input.
  label: z.string().describe("Label"),
  label_override: z.string().nullable().optional().describe("Label Override"),
  approval_status: z
    .enum(["auto_approved", "pending", "approved", "rejected"])
    .default("auto_approved")
    .describe("Approval Status"),
  reason: z.string().nullable().optional().describe("Reason"),
});
export type ProposalDiscountImportRow = z.infer<
  typeof ProposalDiscountImportRowSchema
>;

// ---------------------------------------------------------------------------
// Commission (Tier 6 — depends on job + team_member)
// ---------------------------------------------------------------------------

export const CommissionImportRowSchema = CommissionInsertSchema.omit({
  opportunity_id: true,
  team_member_role_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  opportunity_external_id: z.string().describe("Opportunity External ID"),
  team_member_external_id: z.string().describe("Team Member External ID"),
});
export type CommissionImportRow = z.infer<typeof CommissionImportRowSchema>;

// ---------------------------------------------------------------------------
// Activity Event (Tier 3)
// ---------------------------------------------------------------------------

export const ActivityEventImportRowSchema = ActivityEventInsertSchema.extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  entity_external_id: optionalExternalId().describe("Entity External ID"),
  // ADV-1344: actor identity FK. team_member external_ids are registered under
  // entity_type = "team_member" with entity_id = contact_id, so the generic FK
  // pass (import-service Phase 3) resolves actor_external_id → actor_id (a
  // contact_id). The activity_event directTableHandler then writes that
  // contact_id verbatim to activity_events.actor_id — the import-path actor
  // semantics that core.activity_events_feed_v's OR-bridge LATERAL resolves to
  // a name + headshot (co.id::text = ae.actor_id branch). Without this, demo
  // generators that emit a raw team-member external_id as actor_id leave the
  // feed avatar unresolved.
  actor_external_id: optionalExternalId().describe("Actor External ID"),
  created_at: z.string().optional().describe("Created At"),
});
export type ActivityEventImportRow = z.infer<
  typeof ActivityEventImportRowSchema
>;

// ---------------------------------------------------------------------------
// Appointment (Tier 4 — depends on opportunity + customer + team_member)
// ---------------------------------------------------------------------------

export const AppointmentImportRowSchema = CreateAppointmentSchema.omit({
  customer_role_id: true,
  team_member_role_id: true,
  brand_id: true,
  territory_id: true,
  proposal_id: true,
  // FK resolved from opportunity_external_id by the import pipeline — never an
  // inline-accepted column (mirrors the other *_id omits above).
  opportunity_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  customer_external_id: optionalExternalId().describe("Customer"),
  team_member_external_id: optionalExternalId().describe("Team Member"),
  opportunity_external_id: optionalExternalId().describe("Opportunity"),
  brand_external_id: optionalExternalId().describe("Brand External ID"),
});
export type AppointmentImportRow = z.infer<typeof AppointmentImportRowSchema>;

// ---------------------------------------------------------------------------
// Contact Relationship (Tier 3 — depends on contacts via customers)
// ---------------------------------------------------------------------------

export const ContactRelationshipImportRowSchema = z.object({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  primary_contact_external_id: z.string().describe("Primary Contact"),
  related_contact_external_id: z.string().describe("Related Contact"),
  relationship_type: z
    .enum(contactRelationshipTypes)
    .describe("Relationship Type"),
  notes: z.string().optional().nullable().describe("Notes"),
  is_active: z.boolean().optional().describe("Active"),
});
export type ContactRelationshipImportRow = z.infer<
  typeof ContactRelationshipImportRowSchema
>;

// ---------------------------------------------------------------------------
// Territory Product (Tier 2 — depends on territory + product, parent-scoped)
// ---------------------------------------------------------------------------

export const TerritoryProductImportRowSchema = z.object({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  territory_external_id: z.string().describe("Territory"),
  product_external_id: optionalExternalId().describe("Product"),
  product_category: z
    .string()
    .optional()
    .nullable()
    .describe("Product Category"),
  is_available: z.boolean().optional().describe("Available"),
});
export type TerritoryProductImportRow = z.infer<
  typeof TerritoryProductImportRowSchema
>;

// ---------------------------------------------------------------------------
// Product Distributor Source (Tier 2 — ADR-0052 / ADV-1206).
// core.product_distributor_sources(product_id, trade_partner_contact_role_id, …)
// One product, many distributor (trade_partner) sources. product_external_id
// resolves to the product; distributor_external_id resolves to the distributor
// trade partner's contact, then role-resolution
// (ROLE_FIELD_MAPPINGS.product_distributor_source) writes
// trade_partner_contact_role_id.
// ---------------------------------------------------------------------------

export const ProductDistributorSourceImportRowSchema = z.object({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  product_external_id: z.string().describe("Product External ID"),
  distributor_external_id: z.string().describe("Distributor External ID"),
  distributor_sku: z.string().nullable().optional().describe("Distributor SKU"),
  list_cost: z.number().nullable().optional().describe("List Cost"),
  lead_time_days: z
    .number()
    .int()
    .nullable()
    .optional()
    .describe("Lead Time Days"),
  is_preferred: z.boolean().optional().describe("Preferred"),
});
export type ProductDistributorSourceImportRow = z.infer<
  typeof ProductDistributorSourceImportRowSchema
>;

// ---------------------------------------------------------------------------
// Crew Member (Tier 3 — depends on crew + team_member)
// ---------------------------------------------------------------------------

export const CrewMemberImportRowSchema = z.object({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  crew_external_id: z.string().describe("Crew"),
  team_member_external_id: z.string().describe("Team Member"),
  status: z.string().optional().nullable().describe("Status"),
  custom_fields: z.record(z.unknown()).optional().describe("Custom Fields"),
});
export type CrewMemberImportRow = z.infer<typeof CrewMemberImportRowSchema>;

// ---------------------------------------------------------------------------
// Crew Leader (Tier 3 — depends on crew + team_member)
// ---------------------------------------------------------------------------

export const CrewLeaderImportRowSchema = z.object({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  crew_external_id: z.string().describe("Crew"),
  team_member_external_id: z.string().describe("Team Member"),
  custom_fields: z.record(z.unknown()).optional().describe("Custom Fields"),
});
export type CrewLeaderImportRow = z.infer<typeof CrewLeaderImportRowSchema>;

// ---------------------------------------------------------------------------
// Media triad — Tiers 8/9/10 (ADV-914)
//
// Three coupled entities:
//   - media_submission (tier 8): the envelope. May reference a location.
//   - media_item       (tier 9): per-image row. Carries source_url; the
//                                handler fetches the URL, writes to R2, and
//                                extracts EXIF. submission_id resolves via
//                                the generic FK resolver from
//                                submission_external_id.
//   - media_subject    (tier 10): links a submission to a host entity
//                                (job/opportunity/proposal/warranty). The
//                                (entity_type, entity_external_id) FK is
//                                polymorphic and resolves via the Phase 3.8
//                                pre-pass in import-service.ts, matching
//                                the activity_event / communication pattern.
// ---------------------------------------------------------------------------

export const MediaSubmissionImportRowSchema = MediaSubmissionInsertSchema.omit({
  location_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  location_external_id: optionalExternalId().describe("Location External ID"),
  // ADV-950: Submitter attribution. FK resolver strips `_external_id` →
  // `submitted_by_id` (contact_id), then ROLE_FIELD_MAPPINGS.media_submission
  // converts contact_id → contact_role_id and writes it as `submitted_by` on
  // the resolved row. The handler reads `r.submitted_by` and falls back to
  // `ctx.userId` for pipelines that don't supply a row-level submitter.
  submitted_by_external_id: optionalExternalId().describe(
    "Submitter External ID",
  ),
  // ADV-950: Optional submission timestamp anchored to a job lifecycle date
  // rather than import wall-clock. Falls back to `now()` in the handler.
  submitted_at: z.string().nullable().optional().describe("Submitted At"),
});
export type MediaSubmissionImportRow = z.infer<
  typeof MediaSubmissionImportRowSchema
>;

export const MediaItemImportRowSchema = MediaItemInsertSchema.omit({
  submission_id: true,
  storage_key: true,
  storage_bucket: true,
  public_url: true,
  content_type: true,
  file_size_bytes: true,
  width_px: true,
  height_px: true,
  captured_at: true,
  exif_data: true,
  upload_status: true,
  external_id: true,
  source_url: true,
  // User-supplied metadata that is only meaningful when present — let the
  // handler default these so callers can omit them in lean payloads.
  photo_stage: true,
  caption: true,
  subject_tags: true,
  display_order: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  submission_external_id: z.string().describe("Submission External ID"),
  source_url: z.string().url().describe("Source URL"),
  photo_stage: z
    .enum(["before", "during", "after", "issue", "general"])
    .nullable()
    .optional()
    .describe("Photo Stage"),
  caption: z.string().nullable().optional().describe("Caption"),
  subject_tags: z.array(z.string()).optional().describe("Subject Tags"),
  display_order: z.number().int().optional().describe("Display Order"),
});
export type MediaItemImportRow = z.infer<typeof MediaItemImportRowSchema>;

export const MediaSubjectImportRowSchema = MediaSubjectInsertSchema.omit({
  submission_id: true,
  entity_id: true,
}).extend({
  external_id: RequiredExternalIdSchema.describe("External ID"),
  additional_external_ids: AdditionalExternalIdsSchema.describe(
    "Additional External IDs",
  ),
  submission_external_id: z.string().describe("Submission External ID"),
  entity_external_id: z.string().describe("Entity External ID"),
});
export type MediaSubjectImportRow = z.infer<typeof MediaSubjectImportRowSchema>;

// ---------------------------------------------------------------------------
// Request / Response envelopes
// ---------------------------------------------------------------------------

export const ImportRequestSchema = z.object({
  source: z.string().min(1).describe("Source"),
  // ADV-1055: `mode` historically accepted `"full"` for orphan auto-delete, but
  // the feature was never finished — destructive bulk-reconciliation needs a
  // dry-run → preview → confirm UX that is not on the MVP critical path. The
  // option is removed from every surface; the field stays so the request shape
  // is stable for callers and the DB column (`core.import_jobs.mode`, default
  // `'incremental'`) keeps its existing not-null constraint. Optional + no
  // `.default()` here so callers can omit it without needing to write
  // `mode: "incremental"`; `submitImport` writes "incremental" to the DB when
  // the field is absent (which is the only possible value anyway).
  mode: z.literal("incremental").optional().describe("Mode"),
  brand_id: optionalUuid().describe("Brand"),
  // ADV-893: when true, the import bypasses the normalization workflow
  // (address/phone/contact/property enrichment) and finalizes at
  // status=completed synchronously. Use only for pre-enriched data such
  // as demo fixtures or bulk restores from already-validated sources.
  // The DB column default is `false`, and `submitImport` coerces an
  // omitted field to `false` — no `.default()` here so existing
  // internal callers don't need to pass the field explicitly.
  skip_enrichment: z.boolean().optional().describe("Skip Enrichment"),
  records: z
    .array(z.record(z.unknown()))
    .min(1)
    .max(1000000)
    .describe("Records"),
  notifications: z
    .object({
      callback_url: z.string().url().describe("Callback URL"),
      callback_secret: z.string().min(16).describe("Callback Secret"),
    })
    .optional()
    .describe("Notifications"),
});
export type ImportRequest = z.infer<typeof ImportRequestSchema>;

export const ImportRowErrorSchema = z.object({
  row: z.number().describe("Row Index"),
  external_id: z.string().nullable().describe("External ID"),
  field: z.string().nullable().describe("Field"),
  message: z.string().describe("Message"),
  value: z.unknown().nullable().describe("Value"),
});
export type ImportRowError = z.infer<typeof ImportRowErrorSchema>;

// ADV-1402: rows the reviewer explicitly chose to drop from an
// `awaiting_review` job (the per-row "skip" action), rather than leaving them
// as a silent loss. Recorded into `core.import_jobs.intentionally_skipped_rows`
// when the approve branch applies error-row resolutions, so post-approval there
// is an audit trail that the drop was deliberate. Mirrors `ImportRowErrorSchema`
// plus a `reason` discriminator.
export const IntentionallySkippedRowSchema = z.object({
  row: z.number().int().describe("Row Index"),
  external_id: z.string().nullable().describe("External ID"),
  field: z.string().nullable().describe("Field"),
  value: z.unknown().nullable().describe("Value"),
  reason: z.string().describe("Reason"),
});
export type IntentionallySkippedRow = z.infer<
  typeof IntentionallySkippedRowSchema
>;

// ADV-1084: rows whose parent FK (e.g. customer_external_id on a location
// row) did not resolve. The full original row payload is preserved on
// `row_data` so the user can download the unmatched set as CSV and re-submit
// it after the missing parent records are loaded.
export const UnmatchedRowSchema = z.object({
  row: z.number().int().describe("Row Index"),
  external_id: z.string().nullable().describe("External ID"),
  field: z.string().describe("Field"),
  value: z.string().describe("Value"),
  row_data: z.record(z.unknown()).describe("Row Data"),
});
export type UnmatchedRow = z.infer<typeof UnmatchedRowSchema>;

// Dry-run validation request/response (POST /api/v1/import/{entityType}/validate).
// Accepts a records-only payload plus an optional source for FK resolution.
// Returns the same error shape as the async job pipeline so agents can reuse one parser.

export const ImportValidateRequestSchema = z.object({
  source: z
    .string()
    .min(1)
    .optional()
    .describe(
      "Source identifier (e.g. 'airtable', 'csv_upload') recording where these records originated. Provenance only — FK reference fields like territory_external_id resolve by external_id alone, regardless of which source registered the referenced entity (ADV-836).",
    ),
  records: z
    .array(z.record(z.unknown()))
    .min(1)
    .max(1000)
    .describe("Records to validate (max 1000)"),
});
export type ImportValidateRequest = z.infer<typeof ImportValidateRequestSchema>;

export const ImportValidateResponseSchema = z.object({
  entity_type: ImportEntityType.describe("Entity Type"),
  total_rows: z.number().int().describe("Total Rows"),
  valid_rows: z.number().int().describe("Valid Rows"),
  error_rows: z.number().int().describe("Error Rows"),
  errors: z.array(ImportRowErrorSchema).describe("Errors"),
  warnings: z.array(ImportRowErrorSchema).describe("Warnings"),
});
export type ImportValidateResponse = z.infer<
  typeof ImportValidateResponseSchema
>;

// GET /api/v1/import/external-ids — paginated listing for agents that want to
// know which source records have already been mapped to Adva entities.
export const ExternalIdListResponseSchema = z.object({
  entity_type: z.string().describe("Entity Type"),
  source: z.string().describe("Source"),
  total: z.number().int().describe("Total"),
  limit: z.number().int().describe("Limit"),
  offset: z.number().int().describe("Offset"),
  rows: z
    .array(
      z.object({
        entity_id: z.string().uuid().describe("Entity ID"),
        external_id: z.string().describe("External ID"),
      }),
    )
    .describe("Rows"),
});
export type ExternalIdListResponse = z.infer<
  typeof ExternalIdListResponseSchema
>;

// ---------------------------------------------------------------------------
// Async import job lifecycle
// ---------------------------------------------------------------------------

export const ImportJobStatusEnum = z
  .enum([
    "pending",
    "validating",
    "importing",
    "staged",
    "normalizing",
    "awaiting_review",
    "partially_approved",
    "completed",
    "failed",
    "expired",
    "discarded",
    "cancelled",
  ])
  .describe("Import Job Status");
export type ImportJobStatus = z.infer<typeof ImportJobStatusEnum>;

// ADV-1402 (N3 / R4d): the durable signal recorded when the corrected-row
// re-normalization pass (which runs AFTER a reviewer approves) mints NEW
// review-gated work — address `needs_manual_review` items and/or pending
// contact-dedup candidates. The workflow stamps this onto
// `import_jobs.metadata.post_correction_review`; job-detail surfaces exactly
// this sub-object (never the whole metadata blob) so the review UI can show
// that approving created fresh out-of-band review work.
export const PostCorrectionReviewSchema = z.object({
  manual_review_count: z
    .number()
    .int()
    .nonnegative()
    .describe("Manual Review Count"),
  pending_dedup_count: z
    .number()
    .int()
    .nonnegative()
    .describe("Pending Dedup Count"),
  error_rows: z.number().int().nonnegative().describe("Error Rows"),
  unmatched_count: z.number().int().nonnegative().describe("Unmatched Count"),
  flagged_at: z.string().describe("Flagged At"),
});
export type PostCorrectionReview = z.infer<typeof PostCorrectionReviewSchema>;

export const ImportJobSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  brand_id: z.string().uuid().nullable().optional().describe("Brand"),
  entity_type: ImportEntityType.describe("Entity Type"),
  source: z.string().describe("Source"),
  mode: z.literal("incremental").describe("Mode"),
  status: ImportJobStatusEnum.describe("Status"),
  total_rows: z.number().int().describe("Total Rows"),
  processed_rows: z.number().int().describe("Processed Rows"),
  valid_rows: z.number().int().describe("Valid Rows"),
  error_rows: z.number().int().describe("Error Rows"),
  created_count: z.number().int().describe("Created Count"),
  updated_count: z.number().int().describe("Updated Count"),
  skipped_count: z.number().int().describe("Skipped Count"),
  errors: z.array(ImportRowErrorSchema).describe("Errors"),
  warnings: z.array(ImportRowErrorSchema).describe("Warnings"),
  // ADV-289: CSV headers that had no core-column match, no active custom-field
  // match, and no persisted import_column_mappings row. The wizard surfaces
  // these after a job completes so the user can map or ignore them on the
  // next import.
  unmapped_headers: z.array(z.string()).describe("Unmapped Headers"),
  notification_pending: z
    .boolean()
    .default(false)
    .describe("Notification Pending"),
  // ADV-893: imports with this flag finalize at status=completed without
  // dispatching the normalization workflow.
  skip_enrichment: z.boolean().default(false).describe("Skip Enrichment"),
  normalization_status: z
    .string()
    .nullable()
    .default(null)
    .describe("Normalization Status"),
  normalization_result: z
    .record(z.unknown())
    .nullable()
    .default(null)
    .describe("Normalization Result"),
  review_expires_at: z
    .string()
    .nullable()
    .default(null)
    .describe("Review Expires At"),
  started_at: z.string().nullable().describe("Started At"),
  completed_at: z.string().nullable().describe("Completed At"),
  created_at: z.string().describe("Created At"),
  // ADV-938: liveness + cancellation tracking. last_heartbeat_at is the
  // per-batch checkpoint timestamp the watchdog watches for staleness;
  // cancelled_at / cancelled_reason are set by the cancel endpoint.
  last_heartbeat_at: z
    .string()
    .nullable()
    .default(null)
    .describe("Last Heartbeat At"),
  cancelled_at: z.string().nullable().default(null).describe("Cancelled At"),
  cancelled_reason: z
    .string()
    .nullable()
    .default(null)
    .describe("Cancelled Reason"),
  // ADV-1084: rows that could not be inserted because a referenced parent
  // record (e.g. customer_external_id on a location) did not resolve. Subset
  // of skipped_count. Downloadable via
  // GET /api/v1/import/jobs/:id/unmatched.csv for re-import.
  unmatched_count: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe("Unmatched Count"),
  unmatched_rows: z
    .array(UnmatchedRowSchema)
    .default([])
    .describe("Unmatched Rows"),
  // ADV-1080: customer rows whose email collided with another row in the same
  // job. First occurrence wins (kept by external_id); each subsequent row is
  // imported with `email = null` and a row-level warning naming the kept
  // external_id. The counter lets the dashboard reconcile against the source
  // row count without filtering warnings[].
  duplicates_dropped: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe("Duplicates Dropped"),
  // ADV-1123 / ADR-0045: async digital-asset pipeline coordination. The import
  // tracks how many media assets are still in-flight (pending_media_count) and
  // which Cloudflare Workflow is driving the async download/validate/finalize
  // pipeline (media_workflow_id).
  pending_media_count: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe("Pending Media Count"),
  media_workflow_id: z
    .string()
    .nullable()
    .default(null)
    .describe("Media Workflow ID"),
  // ADV-1018: shared identifier for child jobs that were submitted together
  // as one logical batch (today: restore-from-backup zip + demo-import). Null
  // for one-off wizard uploads. Exposed on the list view so the dashboard can
  // render a single "What happened?" panel per batch instead of one per child.
  batch_id: z.string().uuid().nullable().default(null).describe("Batch ID"),
  // ADV-1402: rows the reviewer explicitly skipped during review (the per-row
  // "skip" action on the Errors tab). Subset of skipped_count. Populated when
  // the approve branch applies error-row resolutions so a deliberate drop
  // leaves an audit trail instead of vanishing silently.
  intentionally_skipped_rows: z
    .array(IntentionallySkippedRowSchema)
    .default([])
    .describe("Intentionally Skipped Rows"),
  // ADV-1402 (R4d): post-correction review-gated work minted when approving
  // corrected rows re-runs normalization (address needs_manual_review / pending
  // dedup). Surfaced from `metadata.post_correction_review` so the review UI can
  // see the durable signal. Null when the approve created no new gated work.
  post_correction_review: PostCorrectionReviewSchema.nullable()
    .default(null)
    .describe("Post Correction Review"),
});
export type ImportJob = z.infer<typeof ImportJobSchema>;

export const ImportJobListItemSchema = ImportJobSchema.omit({
  errors: true,
  warnings: true,
  unmapped_headers: true,
  // ADV-1084: unmatched_rows can be large; list view exposes only the count.
  unmatched_rows: true,
  // ADV-1402: skip-audit detail is review-screen-only; list view stays lean.
  intentionally_skipped_rows: true,
  // ADV-1402 (R4d): post-correction review signal is review-screen-only; the
  // list query never selects it.
  post_correction_review: true,
});
export type ImportJobListItem = z.infer<typeof ImportJobListItemSchema>;

export const ImportSubmitResponseSchema = z.object({
  import_job_id: z.string().uuid().describe("Import Job ID"),
  status: z.literal("pending").describe("Status"),
  entity_type: ImportEntityType.describe("Entity Type"),
  total_rows: z.number().int().describe("Total Rows"),
  poll_url: z.string().describe("Poll URL"),
});
export type ImportSubmitResponse = z.infer<typeof ImportSubmitResponseSchema>;

// ADV-938: cancellation of in-flight import jobs.
export const CancelImportJobRequestSchema = z.object({
  reason: z.string().max(500).optional().describe("Cancellation Reason"),
});
export type CancelImportJobRequest = z.infer<
  typeof CancelImportJobRequestSchema
>;

export const CancelImportJobResponseSchema = z.object({
  id: z.string().uuid().describe("ID"),
  status: ImportJobStatusEnum.describe("Status"),
  cancelled_at: z.string().describe("Cancelled At"),
  cancelled_reason: z.string().nullable().describe("Cancelled Reason"),
});
export type CancelImportJobResponse = z.infer<
  typeof CancelImportJobResponseSchema
>;

// ---------------------------------------------------------------------------
// ADV-1081: Reconciliation report — merges counter columns from the job row
// with a SQL set difference between submitted external_ids (input_data) and
// registered external_ids (core.external_ids) for the same (business_id,
// entity_type, source). Surfaces the silent-drop class of bugs that
// counter math cannot detect on its own.

export const ImportReconciliationResponseSchema = z
  .object({
    job_id: z.string().uuid().describe("Job ID"),
    entity_type: ImportEntityType.describe("Entity Type"),
    source: z.string().describe("Source"),
    status: ImportJobStatusEnum.describe("Status"),
    counters: z
      .object({
        total_rows: z.number().int().describe("Total Rows"),
        processed_rows: z.number().int().describe("Processed Rows"),
        valid_rows: z.number().int().describe("Valid Rows"),
        error_rows: z.number().int().describe("Error Rows"),
        created_count: z.number().int().describe("Created Count"),
        updated_count: z.number().int().describe("Updated Count"),
        skipped_count: z.number().int().describe("Skipped Count"),
        unmatched_count: z.number().int().describe("Unmatched Count"),
        errors_length: z.number().int().describe("Errors Length"),
        warnings_length: z.number().int().describe("Warnings Length"),
      })
      .describe("Counters"),
    accounting: z
      .object({
        submitted: z.number().int().describe("Submitted"),
        accounted_in_counters: z
          .number()
          .int()
          .describe("Accounted In Counters"),
        unaccounted_in_counters: z
          .number()
          .int()
          .describe("Unaccounted In Counters"),
      })
      .describe("Accounting"),
    external_id_diff: z
      .object({
        submitted_count: z.number().int().describe("Submitted Count"),
        registered_count: z.number().int().describe("Registered Count"),
        missing_count: z.number().int().describe("Missing Count"),
        missing_sample: z.array(z.string()).describe("Missing Sample"),
        missing_sample_capped_at: z
          .number()
          .int()
          .describe("Missing Sample Capped At"),
      })
      .describe("External ID Diff"),
  })
  .describe("Import Reconciliation Response");
export type ImportReconciliationResponse = z.infer<
  typeof ImportReconciliationResponseSchema
>;

// ---------------------------------------------------------------------------
// ADV-1157: AI-assisted import diagnosis (Phase 3)
// ---------------------------------------------------------------------------
//
// Output shape of POST /api/v1/import/jobs/{id}/diagnose. The diagnosis is
// produced by an Anthropic call (routed through the adva-ai AI Gateway) that
// is handed a tiered, best-effort context bundle for one import run. The same
// schema backs the MCP `diagnose_import_failure` tool and the web DiagnosisPanel.
// `root_cause.category` is the primary signal the onboard-data skill uses to
// decide data-issue vs platform-bug (was static-rule before this ticket).

export const ImportDiagnosisCategoryEnum = z
  .enum(["data", "platform", "config", "transient"])
  .describe("Root Cause Category");
export type ImportDiagnosisCategory = z.infer<
  typeof ImportDiagnosisCategoryEnum
>;

export const ImportDiagnosisEvidenceKindEnum = z
  .enum(["log", "step", "error_row", "reconciliation", "csv_row"])
  .describe("Evidence Kind");
export type ImportDiagnosisEvidenceKind = z.infer<
  typeof ImportDiagnosisEvidenceKindEnum
>;

export const ImportDiagnosisRemediationActionEnum = z
  .enum([
    "retry_normalization",
    "cancel_job",
    "fix_csv_and_resubmit",
    "file_linear_ticket",
    "contact_support",
    "wait",
  ])
  .describe("Remediation Action");
export type ImportDiagnosisRemediationAction = z.infer<
  typeof ImportDiagnosisRemediationActionEnum
>;

export const ImportDiagnosisRootCauseSchema = z
  .object({
    category: ImportDiagnosisCategoryEnum,
    // Operator-grade root cause: cites pipeline steps, log-event taxonomy, DB
    // identifiers. Shown to super-admins and the MCP/agent path.
    summary: z.string().describe("Summary"),
    // ADV-1165: plain business-domain register for tenant back-office staff —
    // no infra internals (no CF Workflow step names, no `import.*` log taxonomy,
    // no DB index names, no "bug"/"platform" framing). The web DiagnosisPanel
    // renders this for non-super-admins; super-admins still see `summary`.
    user_summary: z.string().describe("User Summary"),
  })
  .describe("Root Cause");
export type ImportDiagnosisRootCause = z.infer<
  typeof ImportDiagnosisRootCauseSchema
>;

export const ImportDiagnosisEvidenceSchema = z
  .object({
    kind: ImportDiagnosisEvidenceKindEnum,
    reference: z.string().describe("Reference"),
    quote: z.string().describe("Quote"),
    // ADV-1165: whether this item is safe to surface to a non-operator. True
    // only when the quote/reference carries no infra internals and is
    // meaningful to back-office staff (typically `csv_row`/`error_row` about
    // their own uploaded data); false for `log`/`step`/`reconciliation`.
    user_safe: z.boolean().describe("User Safe"),
  })
  .describe("Evidence");
export type ImportDiagnosisEvidence = z.infer<
  typeof ImportDiagnosisEvidenceSchema
>;

export const ImportDiagnosisRemediationSchema = z
  .object({
    action: ImportDiagnosisRemediationActionEnum,
    rationale: z.string().describe("Rationale"),
    params: z.record(z.string(), z.unknown()).optional().describe("Params"),
  })
  .describe("Suggested Remediation");
export type ImportDiagnosisRemediation = z.infer<
  typeof ImportDiagnosisRemediationSchema
>;

export const ImportDiagnosisFileTicketSchema = z
  .object({
    recommended: z.boolean().describe("Recommended"),
    title: z.string().optional().describe("Title"),
    body: z.string().optional().describe("Body"),
  })
  .describe("File Linear Ticket");
export type ImportDiagnosisFileTicket = z.infer<
  typeof ImportDiagnosisFileTicketSchema
>;

export const ImportDiagnosisModelMetadataSchema = z
  .object({
    model: z.string().describe("Model"),
    input_tokens: z.number().int().describe("Input Tokens"),
    output_tokens: z.number().int().describe("Output Tokens"),
    cost_usd: z.number().describe("Cost USD"),
  })
  .describe("Model Metadata");
export type ImportDiagnosisModelMetadata = z.infer<
  typeof ImportDiagnosisModelMetadataSchema
>;

export const ImportDiagnosisSchema = z
  .object({
    job_id: z.string().uuid().describe("Job ID"),
    root_cause: ImportDiagnosisRootCauseSchema,
    evidence: z.array(ImportDiagnosisEvidenceSchema).describe("Evidence"),
    suggested_remediation: z
      .array(ImportDiagnosisRemediationSchema)
      .describe("Suggested Remediation"),
    retry_safe: z.boolean().describe("Retry Safe"),
    file_linear_ticket: ImportDiagnosisFileTicketSchema,
    model_metadata: ImportDiagnosisModelMetadataSchema,
  })
  .describe("Import Diagnosis");
export type ImportDiagnosis = z.infer<typeof ImportDiagnosisSchema>;

// The fields the model is asked to fill — model_metadata is appended by the
// service from the API usage, never produced by the model. This is the
// tool-use `input_schema` source the service forces.
export const ImportDiagnosisModelOutputSchema = z
  .object({
    root_cause: ImportDiagnosisRootCauseSchema,
    evidence: z.array(ImportDiagnosisEvidenceSchema).describe("Evidence"),
    suggested_remediation: z
      .array(ImportDiagnosisRemediationSchema)
      .describe("Suggested Remediation"),
    retry_safe: z.boolean().describe("Retry Safe"),
    file_linear_ticket: ImportDiagnosisFileTicketSchema,
  })
  .describe("Import Diagnosis Model Output");
export type ImportDiagnosisModelOutput = z.infer<
  typeof ImportDiagnosisModelOutputSchema
>;

export const DiagnoseImportResponseSchema = z
  .object({
    diagnosis: ImportDiagnosisSchema,
    cached: z.boolean().describe("Cached"),
    created_at: z.string().describe("Created At"),
  })
  .describe("Diagnose Import Response");
export type DiagnoseImportResponse = z.infer<
  typeof DiagnoseImportResponseSchema
>;

// ---------------------------------------------------------------------------
// ADV-1018: AI-assisted import diagnosis at the BATCH grain
// ---------------------------------------------------------------------------
//
// A batch is a set of child import_jobs that share core.import_jobs.batch_id.
// Produced by restoreFromBackupZip and the demo-import path today; per-job
// diagnose still applies inside a batch but the user-facing "What happened?"
// panel reasons across all children to identify the root blocker on the
// IMPORT_TIER_ORDER ladder.
//
// The model is asked for the same output shape as per-job (see
// ImportDiagnosisModelOutputSchema), augmented by deterministic
// service-computed fields: batch_id, child_job_ids[], blocking_tier,
// clusters[] (grouped by root_cause.category), review_url.

export const ImportDiagnosisClusterSchema = z
  .object({
    category: ImportDiagnosisCategoryEnum,
    count: z.number().int().nonnegative().describe("Count"),
    sample_job_ids: z.array(z.string().uuid()).describe("Sample Job IDs"),
    sample_errors: z.array(z.string()).describe("Sample Errors"),
  })
  .describe("Import Diagnosis Cluster");
export type ImportDiagnosisCluster = z.infer<
  typeof ImportDiagnosisClusterSchema
>;

export const BatchDiagnosisSchema = z
  .object({
    batch_id: z.string().uuid().describe("Batch ID"),
    child_job_ids: z.array(z.string().uuid()).describe("Child Job IDs"),
    root_cause: ImportDiagnosisRootCauseSchema,
    evidence: z.array(ImportDiagnosisEvidenceSchema).describe("Evidence"),
    suggested_remediation: z
      .array(ImportDiagnosisRemediationSchema)
      .describe("Suggested Remediation"),
    retry_safe: z.boolean().describe("Retry Safe"),
    file_linear_ticket: ImportDiagnosisFileTicketSchema,
    model_metadata: ImportDiagnosisModelMetadataSchema,
    // Lowest tier from IMPORT_TIER_ORDER with a non-cascade error in this batch.
    // Used by the panel to render "tier N <entity> failed → tier N+1 cascaded".
    blocking_tier: z.number().int().nullable().describe("Blocking Tier"),
    // Grouped by root_cause.category — service-computed from child errors so
    // the panel can render cluster pills sorted by descending count.
    clusters: z.array(ImportDiagnosisClusterSchema).describe("Clusters"),
    // When any child job is in awaiting_review | partially_approved, a
    // deep-link to that job's review page; else null.
    review_url: z.string().nullable().describe("Review URL"),
  })
  .describe("Batch Diagnosis");
export type BatchDiagnosis = z.infer<typeof BatchDiagnosisSchema>;

export const DiagnoseBatchResponseSchema = z
  .object({
    diagnosis: BatchDiagnosisSchema,
    cached: z.boolean().describe("Cached"),
    created_at: z.string().describe("Created At"),
  })
  .describe("Diagnose Batch Response");
export type DiagnoseBatchResponse = z.infer<typeof DiagnoseBatchResponseSchema>;

export const FileImportTicketRequestSchema = z
  .object({
    title: z.string().min(1).max(256).describe("Title"),
    body: z.string().min(1).max(8000).describe("Body"),
  })
  .describe("File Import Ticket Request");
export type FileImportTicketRequest = z.infer<
  typeof FileImportTicketRequestSchema
>;

export const FileImportTicketResponseSchema = z
  .object({
    filed: z.boolean().describe("Filed"),
    identifier: z.string().nullable().describe("Identifier"),
    url: z.string().nullable().describe("URL"),
  })
  .describe("File Import Ticket Response");
export type FileImportTicketResponse = z.infer<
  typeof FileImportTicketResponseSchema
>;

// ---------------------------------------------------------------------------
// ADV-662: Review decision payload for the staged import workflow
// ---------------------------------------------------------------------------
//
// Posted to POST /api/v1/import/jobs/{id}/review while the workflow is
// parked at `awaiting_review`. The endpoint forwards the decision to the
// waiting workflow via the `IMPORT_PIPELINE_SVC.sendReviewDecision` RPC,
// which resumes the durable wait and finalizes the job.

export const ImportReviewDedupResolutionSchema = z.object({
  candidate_id: z.string().uuid().describe("Candidate ID"),
  resolution: z
    .enum(["merge_a", "merge_b", "keep_both"])
    .describe("Resolution"),
});
export type ImportReviewDedupResolution = z.infer<
  typeof ImportReviewDedupResolutionSchema
>;

// ADV-1402: a corrected error row. `row` is the index into the job's
// `input_data`; `values` are the field overrides the reviewer entered inline.
// The approve branch re-hydrates `input_data[row]`, applies `values`,
// re-validates, and imports the row if it now passes.
export const ImportReviewCorrectedRowSchema = z.object({
  row: z.number().int().describe("Row Index"),
  values: z.record(z.unknown()).describe("Corrected Values"),
});
export type ImportReviewCorrectedRow = z.infer<
  typeof ImportReviewCorrectedRowSchema
>;

// ADV-1402: an error row the reviewer explicitly chose to skip/delete.
export const ImportReviewSkippedRowSchema = z.object({
  row: z.number().int().describe("Row Index"),
});
export type ImportReviewSkippedRow = z.infer<
  typeof ImportReviewSkippedRowSchema
>;

export const ImportReviewChangesSchema = z
  .object({
    dedup_resolutions: z
      .array(ImportReviewDedupResolutionSchema)
      .optional()
      .describe("Dedup Resolutions"),
    address_reverts: z
      .array(z.string().uuid())
      .optional()
      .describe("Address Reverts"),
    // ADV-1402: error rows the reviewer corrected inline; re-validated and
    // re-imported on approve.
    corrected_rows: z
      .array(ImportReviewCorrectedRowSchema)
      .optional()
      .describe("Corrected Rows"),
    // ADV-1402: error rows the reviewer explicitly skipped; recorded into
    // intentionally_skipped_rows on approve.
    skipped_rows: z
      .array(ImportReviewSkippedRowSchema)
      .optional()
      .describe("Skipped Rows"),
  })
  .describe("Review Changes");
export type ImportReviewChanges = z.infer<typeof ImportReviewChangesSchema>;

// ADV-1402: POST /import/jobs/{id}/errors/revalidate — live pass/fail feedback
// for a corrected error row before approval. The UI sends the row index + the
// edited field values; the server re-hydrates the full original row from
// input_data, applies the values, and re-runs validation.
export const ImportRevalidateRowRequestSchema = z
  .object({
    row: z.number().int().nonnegative().describe("Row Index"),
    values: z.record(z.unknown()).describe("Corrected Values"),
  })
  .describe("Import Revalidate Row Request");
export type ImportRevalidateRowRequest = z.infer<
  typeof ImportRevalidateRowRequestSchema
>;

export const ImportRevalidateRowResponseSchema = z
  .object({
    valid: z.boolean().describe("Valid"),
    errors: z.array(ImportRowErrorSchema).describe("Errors"),
    warnings: z.array(ImportRowErrorSchema).describe("Warnings"),
  })
  .describe("Import Revalidate Row Response");
export type ImportRevalidateRowResponse = z.infer<
  typeof ImportRevalidateRowResponseSchema
>;

export const ImportReviewDecisionSchema = z
  .object({
    approved: z.boolean().describe("Approved"),
    partial: z
      .boolean()
      .optional()
      .describe(
        "When true, terminal status becomes `partially_approved` instead of `completed`.",
      ),
    approved_record_ids: z
      .array(z.string().uuid())
      .optional()
      .describe("Approved Record IDs"),
    changes: ImportReviewChangesSchema.optional().describe("Changes"),
  })
  .describe("Import Review Decision");
export type ImportReviewDecision = z.infer<typeof ImportReviewDecisionSchema>;

// ADV-663: Response shape for POST /import/jobs/:id/retry-normalization.
// Returned synchronously by the API after the workflow is re-dispatched —
// `job_status` is the freshly-reset import_jobs.status, `workflow_status`
// is the new Cloudflare Workflow instance status (typically "running").
// Clients poll GET /import/jobs/:id afterwards to follow completion.
export const ImportRetryNormalizationResponseSchema = z
  .object({
    ok: z.boolean().describe("Ok"),
    job_status: z.string().describe("Job Status"),
    workflow_status: z.string().describe("Workflow Status"),
  })
  .describe("Import Retry Normalization Response");
export type ImportRetryNormalizationResponse = z.infer<
  typeof ImportRetryNormalizationResponseSchema
>;

// ---------------------------------------------------------------------------
// ADV-668: Address-fix rows for the review screen Address Fixes + Map tabs
// ---------------------------------------------------------------------------
//
// Joins core.address_normalization_log to core.locations so the review UI
// can render before/after rows, decision state, and geocoded pins from a
// single fetch. `is_failed` is computed server-side as the union of "no
// coordinates returned" and "geocode_accuracy explicitly says failed".

export const ImportAddressFixSchema = z.object({
  id: z.string().uuid().describe("ID"),
  location_id: z.string().uuid().nullable().describe("Location"),
  original_input: z.string().describe("Original Input"),
  original_source: z.string().nullable().describe("Original Source"),
  normalized_street: z.string().nullable().describe("Normalized Street"),
  normalized_city: z.string().nullable().describe("Normalized City"),
  normalized_state: z.string().nullable().describe("Normalized State"),
  normalized_zip: z.string().nullable().describe("Normalized Zip"),
  precision_type: z.string().nullable().describe("Precision Type"),
  confidence: z.string().nullable().describe("Confidence"),
  fields_changed: z.array(z.string()).nullable().describe("Fields Changed"),
  zip_corrected: z.boolean().nullable().describe("Zip Corrected"),
  decision: z.enum(["approved", "reverted"]).nullable().describe("Decision"),
  decided_at: z.string().nullable().describe("Decided At"),
  formatted_address: z.string().nullable().describe("Formatted Address"),
  latitude: z.number().nullable().describe("Latitude"),
  longitude: z.number().nullable().describe("Longitude"),
  geocode_accuracy: z.string().nullable().describe("Geocode Accuracy"),
  geocode_source: z.string().nullable().describe("Geocode Source"),
  is_failed: z.boolean().describe("Is Failed"),
  created_at: z.string().describe("Created At"),
});
export type ImportAddressFix = z.infer<typeof ImportAddressFixSchema>;

export const ImportAddressFixListQuerySchema = z.object({
  limit: clampedLimit(500, 100).describe("Limit"),
  offset: z.coerce.number().int().min(0).default(0).describe("Offset"),
});
export type ImportAddressFixListQuery = z.infer<
  typeof ImportAddressFixListQuerySchema
>;

export const ImportAddressFixListResponseSchema = z
  .object({
    fixes: z.array(ImportAddressFixSchema).describe("Fixes"),
    total: z.number().int().describe("Total"),
    limit: z.number().int().describe("Limit"),
    offset: z.number().int().describe("Offset"),
  })
  .describe("Import Address Fix List Response");
export type ImportAddressFixListResponse = z.infer<
  typeof ImportAddressFixListResponseSchema
>;

// ---------------------------------------------------------------------------
// Import column mappings (ADV-289)
// ---------------------------------------------------------------------------
//
// Persists a user's decision about where a raw CSV header goes: a core column,
// a custom field (`field_key`), or an explicit ignore. The next import by the
// same business reads the mapping and applies it before Zod validation — no
// re-asking, no silent drops.

export const ImportColumnMappingTargetKind = z
  .enum(["core", "custom", "ignore"])
  .describe("Target Kind");
export type ImportColumnMappingTargetKind = z.infer<
  typeof ImportColumnMappingTargetKind
>;

export const ImportColumnMappingSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  entity_type: ImportEntityType.describe("Entity Type"),
  source_header: z.string().describe("Source Header"),
  target_kind: ImportColumnMappingTargetKind.describe("Target Kind"),
  target_field: z.string().nullable().describe("Target Field"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  last_used_at: z.string().describe("Last Used At"),
});
export type ImportColumnMapping = z.infer<typeof ImportColumnMappingSchema>;

export const ImportColumnMappingInsertSchema = ImportColumnMappingSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  last_used_at: true,
}).extend({
  business_id: optionalUuid().describe("Business"),
  account_id: optionalUuid().describe("Account"),
});
export type ImportColumnMappingInsert = z.infer<
  typeof ImportColumnMappingInsertSchema
>;

// ---------------------------------------------------------------------------
// API Key schemas (ADV-295)
// ---------------------------------------------------------------------------
//
// Scopes are ADDITIVE RESTRICTIONS on the owning user's RBAC permissions —
// never grants. A key with scope `core:write` held by a user whose role only
// grants read access still cannot write. The scope narrows what the key can
// reach; the user's role still governs whether the request is allowed.
//
//   *              — full access (subject to the user's RBAC)
//   core:read      — read endpoints under /api/v1/core/*
//   core:write     — write endpoints under /api/v1/core/*
//   sales:read     — read under /api/v1/sales/*
//   sales:write    — write under /api/v1/sales/*
//   ops:read       — read under /api/v1/ops/*
//   ops:write      — write under /api/v1/ops/*
//   finance:read   — read under /api/v1/finance/*
//   finance:write  — write under /api/v1/finance/*
//   admin:read     — read admin + audit endpoints (super_admin only)
//   admin:write    — write admin endpoints (super_admin only)
//   import         — /api/v1/import/* (legacy shorthand; equivalent to a
//                    narrow write scope on import routes)

export const ApiKeyScopeSchema = z
  .enum([
    "*",
    "core:read",
    "core:write",
    "sales:read",
    "sales:write",
    "ops:read",
    "ops:write",
    "finance:read",
    "finance:write",
    "admin:read",
    "admin:write",
    "import",
    "inbound:write",
    // ADV-882: destructive ops (business reset, etc.). NOT covered by the
    // `"*"` superscope — keys must explicitly opt in. See
    // apps/api/src/middleware/api-key-auth.ts WILDCARD_EXCLUDED_SCOPES.
    "destructive_ops",
  ])
  .describe("Scope");
export type ApiKeyScope = z.infer<typeof ApiKeyScopeSchema>;

export const API_KEY_SCOPES = ApiKeyScopeSchema.options;

// ADV-998 — Two key namespaces share core.api_keys, distinguished by the
// raw-key prefix:
//   * 'user'    → `adva_k_*`   (default; programmatic access to /api/v1/*)
//   * 'inbound' → `adva_inb_*` (catch-all inbound webhook endpoint)
export const ApiKeyKindSchema = z
  .enum(["user", "inbound"])
  .describe("Key Kind");
export type ApiKeyKind = z.infer<typeof ApiKeyKindSchema>;

export const ApiKeyCreateSchema = z.object({
  name: z.string().min(1).max(100).describe("Name"),
  // Default to full access ("*") — intentional. Most users creating a key want
  // the same access their account would have; they can narrow later. The UI
  // surfaces this default prominently so it's not accidental.
  scopes: z.array(ApiKeyScopeSchema).default(["*"]).describe("Scopes"),
  expires_at: z.string().datetime().optional().describe("Expires At"),
  // ADV-998 — When `kind: 'inbound'`, the service mints an `adva_inb_*` key
  // intended for the catch-all webhook endpoint and forces the scope to
  // `['inbound:write']`. The service treats a missing value as `'user'`
  // for backwards compatibility; keeping this optional (no schema default)
  // keeps existing callers source-compatible.
  kind: ApiKeyKindSchema.optional().describe("Kind"),
});
export type ApiKeyCreate = z.infer<typeof ApiKeyCreateSchema>;

export const ApiKeySchema = z.object({
  id: z.string().uuid().describe("ID"),
  user_id: z.string().uuid().describe("Owner User"),
  name: z.string().describe("Name"),
  key_prefix: z.string().describe("Key Prefix"),
  scopes: z.array(ApiKeyScopeSchema).describe("Scopes"),
  rate_limit_per_hour: z.number().int().describe("Rate Limit Per Hour"),
  expires_at: z.string().nullable().describe("Expires At"),
  last_used_at: z.string().nullable().describe("Last Used At"),
  is_active: z.boolean().describe("Active"),
  created_at: z.string().describe("Created At"),
});
export type ApiKey = z.infer<typeof ApiKeySchema>;

export const ApiKeyCreateResponseSchema = z.object({
  api_key: ApiKeySchema.describe("API Key"),
  raw_key: z.string().describe("Raw Key"),
});
export type ApiKeyCreateResponse = z.infer<typeof ApiKeyCreateResponseSchema>;

export const ApiKeyUpdateSchema = z.object({
  name: z.string().min(1).max(100).optional().describe("Name"),
  scopes: z.array(ApiKeyScopeSchema).optional().describe("Scopes"),
  is_active: z.boolean().optional().describe("Active"),
  rate_limit_per_hour: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe("Rate Limit Per Hour"),
});
export type ApiKeyUpdate = z.infer<typeof ApiKeyUpdateSchema>;

// ---------------------------------------------------------------------------
// CSV upload + validation schemas (ADV-243)
// ---------------------------------------------------------------------------

export const UploadedFileSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  api_key_id: z.string().uuid().nullable().describe("API Key"),
  uploaded_by_user_id: z
    .string()
    .uuid()
    .nullable()
    .describe("Uploaded By User"),
  entity_type: ImportEntityType.describe("Entity Type"),
  storage_backend: z
    .enum(["r2", "supabase", "inline"])
    .describe("Storage Backend"),
  storage_bucket: z.string().describe("Storage Bucket"),
  storage_key: z.string().describe("Storage Key"),
  filename: z.string().describe("Filename"),
  mime_type: z.string().nullable().describe("Mime Type"),
  file_hash: z.string().describe("File Hash"),
  byte_size: z.number().int().nonnegative().describe("Byte Size"),
  row_count: z.number().int().nullable().describe("Row Count"),
  metadata: z.record(z.unknown()).describe("Metadata"),
  uploaded_at: z.string().describe("Uploaded At"),
  created_at: z.string().describe("Created At"),
});
export type UploadedFile = z.infer<typeof UploadedFileSchema>;

export const CsvCoercionErrorSchema = z.object({
  row: z.number().int().describe("Row Index"),
  column: z.string().describe("Column"),
  value: z.string().nullable().describe("Value"),
  reason: z.string().describe("Reason"),
});
export type CsvCoercionError = z.infer<typeof CsvCoercionErrorSchema>;

export const CsvColumnMappingSchema = z.object({
  matched: z.array(z.string()).describe("Matched"),
  unmatched_headers: z.array(z.string()).describe("Unmatched Headers"),
  missing_required: z.array(z.string()).describe("Missing Required"),
  extra_columns: z.array(z.string()).describe("Extra Columns"),
  // ADV-289 bucketed additions. Callers that pre-date custom-field routing
  // keep using `matched`/`unmatched_headers`/`extra_columns`; new wizard code
  // reads these instead. All three are optional so existing API consumers
  // continue to parse responses without changes.
  custom_mapped: z
    .array(
      z.object({
        header: z.string().describe("Header"),
        field_key: z.string().describe("Field Key"),
      }),
    )
    .optional()
    .describe("Custom Mapped"),
  ignored: z.array(z.string()).optional().describe("Ignored"),
  unmapped: z.array(z.string()).optional().describe("Unmapped"),
});
export type CsvColumnMapping = z.infer<typeof CsvColumnMappingSchema>;

export const CsvValidationResponseSchema = z.object({
  entity_type: ImportEntityType.describe("Entity Type"),
  total_rows: z.number().int().describe("Total Rows"),
  valid_rows: z.number().int().describe("Valid Rows"),
  error_rows: z.number().int().describe("Error Rows"),
  column_mapping: CsvColumnMappingSchema.describe("Column Mapping"),
  coercion_errors: z.array(CsvCoercionErrorSchema).describe("Coercion Errors"),
  errors: z.array(ImportRowErrorSchema).describe("Errors"),
  preview: z.array(z.record(z.unknown())).describe("Preview"),
});
export type CsvValidationResponse = z.infer<typeof CsvValidationResponseSchema>;

/**
 * ADV-1110 — non-fatal upload-time warning. Currently the only code is
 * `STAGED_PARENTS_PENDING`, raised when the upload's entity type has at least
 * one parent entity type with a job still in `awaiting_review` or
 * `partially_approved` for the same business — child rows uploaded now would
 * bucket into `unmatched_rows[]` because the FK resolver only sees `completed`
 * parents. The upload is still accepted; the warning is advisory.
 */
export const ImportUploadWarningCodeSchema = z
  .enum(["STAGED_PARENTS_PENDING"])
  .describe("Warning Code");
export type ImportUploadWarningCode = z.infer<
  typeof ImportUploadWarningCodeSchema
>;

export const ImportUploadWarningSchema = z.object({
  code: ImportUploadWarningCodeSchema.describe("Code"),
  message: z.string().describe("Message"),
  blocking_jobs: z
    .array(z.string().uuid())
    .optional()
    .describe("Blocking Jobs"),
  suggested_action: z.string().optional().describe("Suggested Action"),
});
export type ImportUploadWarning = z.infer<typeof ImportUploadWarningSchema>;

export const CsvUploadResponseSchema = z.object({
  import_job_id: z.string().uuid().describe("Import Job ID"),
  uploaded_file_id: z.string().uuid().describe("Uploaded File ID"),
  entity_type: ImportEntityType.describe("Entity Type"),
  poll_url: z.string().describe("Poll URL"),
  // ADV-1110: optional pre-flight warnings — present when uploading a child
  // entity while one of its parents has an `awaiting_review` job.
  warnings: z.array(ImportUploadWarningSchema).optional().describe("Warnings"),
});
export type CsvUploadResponse = z.infer<typeof CsvUploadResponseSchema>;

// ---------------------------------------------------------------------------
// Entity-to-schema registry
// ---------------------------------------------------------------------------

export const IMPORT_ROW_SCHEMAS = {
  product: ProductImportRowSchema,
  territory: TerritoryImportRowSchema,
  trade_partner: TradePartnerImportRowSchema,
  customer: CustomerImportRowSchema,
  team_member: TeamMemberImportRowSchema,
  crew: CrewImportRowSchema,
  location: LocationImportRowSchema,
  opportunity: OpportunityImportRowSchema,
  proposal: ProposalImportRowSchema,
  proposal_item: ProposalItemFlatImportRowSchema,
  job: JobImportRowSchema,
  job_item: JobItemFlatImportRowSchema,
  invoice: InvoiceImportRowSchema,
  warranty: WarrantyImportRowSchema,
  warranty_term: WarrantyTermImportRowSchema,
  warranty_claim: WarrantyClaimImportRowSchema,
  service_plan: ServicePlanImportRowSchema,
  service_agreement: ServiceAgreementImportRowSchema,
  transaction: TransactionImportRowSchema,
  commission_plan: CommissionPlanImportRowSchema,
  brand: BrandImportRowSchema,
  brand_assignment: BrandAssignmentImportRowSchema,
  promotion: PromotionImportRowSchema,
  proposal_discount: ProposalDiscountImportRowSchema,
  commission: CommissionImportRowSchema,
  activity_event: ActivityEventImportRowSchema,
  appointment: AppointmentImportRowSchema,
  contact_relationship: ContactRelationshipImportRowSchema,
  territory_product: TerritoryProductImportRowSchema,
  product_distributor_source: ProductDistributorSourceImportRowSchema,
  warranty_term_products: WarrantyTermProductImportRowSchema,
  crew_member: CrewMemberImportRowSchema,
  crew_leader: CrewLeaderImportRowSchema,
  communication: CommunicationImportRowSchema,
  media_submission: MediaSubmissionImportRowSchema,
  media_item: MediaItemImportRowSchema,
  media_subject: MediaSubjectImportRowSchema,
} as const satisfies Record<ImportEntityType, z.ZodType>;
